import { useEffect, useRef, useState } from 'react';
import Cookies from 'universal-cookie';
import drop_icon from './drop_icon.svg';
import useGeneralStore from '../../store/general';
import { useResponseHandler } from '../../common/auth';
import { ButtonClose } from '@lk-gtcom/ecomlab-components';
import "./DropZoneDirectory.scss";

const DropZoneDirectory = ({ accept = false, id, setLoadList, update }) => {
    const { handleStatusCode } = useResponseHandler();
    const cookies = new Cookies();
    const token = cookies.get('auth');
    const selectedSpace = useGeneralStore((state) => state.selectedSpace);
    const abortControllersRef = useRef({});
    const fileInputRef = useRef(null);
    const [uploads, setUploads] = useState({});
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        return () => {
            Object.values(abortControllersRef.current).forEach(controller => controller?.abort());
        };
    }, []);

    // Автоматически закрываем подсказку, когда нет активных загрузок
    useEffect(() => {
        const hasActiveUploads = Object.values(uploads).some(u => u.status === 'uploading');
        const hasErrors = Object.values(uploads).some(u => u.status === 'error');

        if (!hasActiveUploads && !hasErrors && Object.keys(uploads).length > 0) {
            // Таймер для автоматического закрытия через 2 секунды после завершения
            const timer = setTimeout(() => {
                setIsOpen(false);
                // Очищаем список успешных загрузок через 3 секунды
                setTimeout(() => {
                    const onlySuccess = Object.values(uploads).every(u => u.status === 'success');
                    if (onlySuccess) {
                        setUploads({});
                        setLoadList([]);
                    }
                }, 1000);
            }, 2000);

            return () => clearTimeout(timer);
        }
    }, [uploads]);

    const updateUploadStatus = (fileId, updates) => {
        setUploads(prev => ({ ...prev, [fileId]: { ...prev[fileId], ...updates } }));
        setLoadList(prev => {
            const index = prev.findIndex(item => item.id === fileId);
            if (index === -1) return prev;
            const newList = [...prev];
            newList[index] = { ...newList[index], ...updates };
            return newList;
        });
    };

    const fetchPostImg = async (file, fileId) => {
        const fd = new FormData();
        fd.append('file', file);

        const xhr = new XMLHttpRequest();
        abortControllersRef.current[fileId] = { abort: () => xhr.abort() };

        xhr.upload.onprogress = (e) => {
            if (e.lengthComputable) {
                const percent = Math.round((e.loaded / e.total) * 100);
                updateUploadStatus(fileId, { progress: percent });
            }
        };

        return new Promise((resolve, reject) => {
            xhr.onload = () => {
                if (xhr.status === 200) {
                    updateUploadStatus(fileId, { status: 'success', progress: 100 });
                    update();
                    resolve();
                } else {
                    handleStatusCode(xhr.status);
                    updateUploadStatus(fileId, { status: 'error', error: `Ошибка ${xhr.status}` });
                    reject();
                }
            };
            xhr.onerror = () => {
                updateUploadStatus(fileId, { status: 'error', error: 'Ошибка сети' });
                reject();
            };
            xhr.onabort = () => {
                setUploads(prev => { const newPrev = { ...prev }; delete newPrev[fileId]; return newPrev; });
                reject();
            };

            xhr.open('POST', `https://apps2.ecomru.ru:4460/api/v1/file/upload-to-directory/${id}`);
            xhr.setRequestHeader('space', selectedSpace?.value);
            xhr.setRequestHeader('Authorization', `Bearer ${token}`);
            xhr.setRequestHeader('Accept', 'application/json');
            xhr.setRequestHeader('access', 'public');
            xhr.send(fd);
        });
    };

    const handleRetry = async (fileId) => {
        const file = uploads[fileId]?.file;
        if (!file) return;
        updateUploadStatus(fileId, { status: 'uploading', progress: 0, error: null });
        setIsOpen(true);
        await fetchPostImg(file, fileId);
    };

    const handleRemove = (fileId) => {
        if (uploads[fileId]?.status === 'uploading') {
            abortControllersRef.current[fileId]?.abort();
        }
        setUploads(prev => {
            const newPrev = { ...prev };
            delete newPrev[fileId];
            return newPrev;
        });
        setLoadList(prev => prev.filter(item => item.id !== fileId));
    };

    const handleClearAll = () => {
        Object.keys(uploads).forEach(fileId => {
            if (uploads[fileId]?.status === 'uploading') {
                abortControllersRef.current[fileId]?.abort();
            }
        });
        setUploads({});
        setLoadList([]);
        setIsOpen(false);
    };

    const handleFileInput = async (e) => {
        const files = Array.from(e.target.files);
        if (!files.length) return;

        // Простая проверка размера файла (200 МБ = 200 * 1024 * 1024 байт)
        const MAX_SIZE = 200 * 1024 * 1024; // 200MB
        const validFiles = [];
        const invalidFiles = [];

        files.forEach(file => {
            if (file.size <= MAX_SIZE) {
                validFiles.push(file);
            } else {
                invalidFiles.push(file.name);
            }
        });

        // Показываем alert если есть файлы больше 200 МБ
        if (invalidFiles.length > 0) {
            const fileNames = invalidFiles.join('\n• ');
            alert(`Следующие файлы превышают максимальный размер (200 МБ) и не будут загружены:\n\n• ${fileNames}`);
        }

        // Если нет валидных файлов, выходим
        if (validFiles.length === 0) {
            if (fileInputRef.current) fileInputRef.current.value = '';
            return;
        }

        // Создаем записи для валидных файлов
        const newUploads = {};
        validFiles.forEach(file => {
            const fileId = `${file.name}_${Date.now()}_${Math.random()}`;
            newUploads[fileId] = {
                id: fileId,
                name: file.name,
                status: 'uploading',
                progress: 0,
                file: file
            };
        });

        // Добавляем все файлы в состояние сразу
        setUploads(prev => ({ ...prev, ...newUploads }));

        // Добавляем все файлы в loadList сразу
        setLoadList(prev => [
            ...prev,
            ...Object.values(newUploads).map(u => ({
                id: u.id,
                name: u.name,
                status: 'uploading',
                progress: 0
            }))
        ]);

        setIsOpen(true);

        // Запускаем загрузку всех файлов параллельно
        const uploadPromises = Object.entries(newUploads).map(([fileId, upload]) =>
            fetchPostImg(upload.file, fileId).catch(err => {
                console.error('Upload failed:', err);
            })
        );

        await Promise.allSettled(uploadPromises);

        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const successCount = Object.values(uploads).filter(u => u.status === 'success').length;
    const errorCount = Object.values(uploads).filter(u => u.status === 'error').length;
    const activeCount = Object.values(uploads).filter(u => u.status === 'uploading').length;

    return (
        <div className='drop-zone'>
            <img src={drop_icon} alt='icon' />
            <p>Перетащите файлы сюда или нажмите, чтобы <span>загрузить.</span> <br/>(Максимальный размер файла: 200 МБ)</p>
            <input ref={fileInputRef} onChange={handleFileInput} className='input-file' type='file' multiple {...(accept ? accept : {})} />

            {/* Подсказка показывается только когда есть загрузки и она открыта */}
            {
                isOpen && Object.keys(uploads).length > 0 &&
                <div className="upload-list-panel">
                    <div className="upload-list-panel__panel-header">
                        <div className="upload-list-panel__header-title">
                            <span>Загрузка файлов</span>
                            <div className="upload-list-panel__stats">
                                {successCount > 0 && <span className="upload-list-panel__stat-success">✓ {successCount}</span>}
                                {errorCount > 0 && <span className="upload-list-panel__stat-error">✗ {errorCount}</span>}
                                {activeCount > 0 && <span className="upload-list-panel__stat-active">⟳ {activeCount}</span>}
                            </div>
                        </div>
                        <div className="upload-list-panel__header-actions">
                            <button className="upload-list-panel__clear-btn" onClick={handleClearAll} title="Очистить всё">🗑</button>
                            <ButtonClose
                                onClick={() => setIsOpen(false)}
                                xs
                            />
                        </div>
                    </div>

                    <div className="files-list">
                        {Object.entries(uploads).map(([id, upload]) => (
                            <div key={id} className={`upload-list-panel__list-item ${upload.status}`}>
                                <div className="upload-list-panel__item-header">
                                    <div className="upload-list-panel__file-info">
                                        <span className="upload-list-panel__file-name" title={upload.name}>
                                            {upload.name}
                                        </span>
                                    </div>
                                    <div className="upload-list-panel__item-actions">
                                        {upload.status === 'error' && (
                                            <button className="upload-list-panel__retry-btn" onClick={() => handleRetry(id)} title="Повторить">
                                                ↻
                                            </button>
                                        )}
                                        <ButtonClose
                                            onClick={() => handleRemove(id)}
                                            xs
                                        />
                                    </div>
                                </div>

                                {upload.status === 'uploading' &&
                                    <div className="upload-list-panel__progress-wrapper">
                                        <div className="upload-list-panel__progress-bar">
                                            <div className="upload-list-panel__progress-fill" style={{ width: `${upload.progress}%` }} />
                                        </div>
                                        <span className="upload-list-panel__progress-text">{upload.progress}%</span>
                                    </div>
                                }

                                {upload.status === 'error' && upload.error &&
                                    <div className="upload-list-panel__error-message">{upload.error}</div>
                                }

                                {upload.status === 'success' &&
                                    <div className="upload-list-panel__success-message">Файл успешно загружен</div>
                                }
                            </div>
                        ))}
                    </div>
                </div>
            }
        </div>
    );
};

export { DropZoneDirectory };